import { useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@mantine/core';
import { campusById, serviceTimesFor } from '@/lib/lookups';
import { riderMissingStep } from '@/store/ridesWizard';
import { useCopy } from '@/lib/settings';
import { StepHeader } from '@/components/ui/StepHeader';
import { StickyActionBar } from '@/components/ui/StickyActionBar';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { CampusPicker } from '@/components/rides/CampusPicker';
import { ServiceTimePicker } from '@/components/rides/ServiceTimePicker';
import { needsServiceStep, useRiderFlow } from './_shared';

type Part = 'from' | 'to' | 'service';

/**
 * Find a ride, the route, one question per screen: pick-up campus (?q=from), drop-off campus
 * (?q=to, the event's own campus is suggested) and, for Sunday services, the service (?q=service).
 * Choosing a card moves on; coming back shows the choice with a Continue button.
 */
export default function RideRoutePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useCopy();
  const f = useRiderFlow();
  const { rider, setRider, lookups, event, drop, settings } = f;
  const q = params.get('q');
  const part: Part = q === 'to' || q === 'service' ? q : 'from';

  // Suggest the event's campus as the drop-off (one tap on Continue).
  const suggested = event?.locationID ?? null;
  useEffect(() => {
    if (part === 'to' && rider.dropOffLocationId == null && suggested != null && suggested !== rider.collectionLocationId && lookups.data?.campuses.some((c) => c.locationID === suggested && c.isActive)) {
      setRider({ dropOffLocationId: suggested, serviceTimeId: null, chosenAvailabilityId: null });
    }
  }, [part, rider.dropOffLocationId, rider.collectionLocationId, suggested, lookups.data, setRider]);

  const missing = riderMissingStep(rider, 'route');
  if (missing) return <Navigate to={missing} replace />;
  if (part !== 'from' && rider.collectionLocationId == null) return <Navigate to="/ride/route" replace />;
  if (part === 'service' && rider.dropOffLocationId == null) return <Navigate to="/ride/route?q=to" replace />;
  if (part === 'service' && lookups.data && f.events.data && !f.needsService) return <Navigate to="/ride/party" replace />;

  const afterTo = (dropId: number) => (needsServiceStep(event, campusById(lookups.data, dropId), settings) ? '/ride/route?q=service' : '/ride/party');

  const pickFrom = (id: number) => {
    setRider({ collectionLocationId: id, dropOffLocationId: rider.dropOffLocationId === id ? null : rider.dropOffLocationId, chosenAvailabilityId: null });
    navigate('/ride/route?q=to');
  };
  const pickTo = (id: number) => {
    if (id !== rider.dropOffLocationId) setRider({ dropOffLocationId: id, serviceTimeId: null, chosenAvailabilityId: null });
    navigate(afterTo(id));
  };
  const pickService = (id: number | null) => {
    setRider({ serviceTimeId: id, chosenAvailabilityId: null });
    navigate('/ride/party');
  };

  const head = {
    from: { step: f.stepOf('from'), title: 'Where should we pick you up?', lede: t('ride.from_intro'), backTo: '/ride/event' },
    to: { step: f.stepOf('to'), title: 'Which campus are you going to?', lede: t('ride.to_intro'), backTo: '/ride/route' },
    service: { step: f.stepOf('service'), title: 'Which service are you going to?', lede: t('ride.service_intro', undefined, { campus: drop?.name ?? 'this campus' }), backTo: '/ride/route?q=to' },
  }[part];

  const canContinue =
    part === 'from' ? rider.collectionLocationId != null : part === 'to' ? rider.dropOffLocationId != null && rider.dropOffLocationId !== rider.collectionLocationId : true;
  const onContinue = () => {
    if (part === 'from') navigate('/ride/route?q=to');
    else if (part === 'to' && rider.dropOffLocationId != null) navigate(afterTo(rider.dropOffLocationId));
    else navigate('/ride/party');
  };

  return (
    <div className="rides-main rides-flow">
      <StepHeader step={head.step} total={f.total} title={head.title} lede={head.lede} backTo={head.backTo} chips={f.chips(part)} />
      <div className="rides-stepbody" key={part}>
      {lookups.isLoading && <LoadingState label="Loading campuses" variant="cards" count={3} />}
      {lookups.error && <ErrorState title="We couldn't load the campuses" error={lookups.error} onRetry={() => lookups.refetch()} retrying={lookups.isFetching} />}
      {lookups.data && part === 'from' && (
        <CampusPicker campuses={lookups.data.campuses} value={rider.collectionLocationId} onChange={pickFrom} ariaLabel="Pick-up campus" navigates />
      )}
      {lookups.data && part === 'to' && (
        <CampusPicker
          campuses={lookups.data.campuses}
          value={rider.dropOffLocationId}
          onChange={pickTo}
          excludeId={rider.collectionLocationId}
          hideExcluded
          ariaLabel="Drop-off campus"
          navigates
        />
      )}
      {lookups.data && part === 'service' && (
        <ServiceTimePicker
          variant="cards"
          navigates
          times={serviceTimesFor(drop)}
          value={rider.serviceTimeId}
          onChange={pickService}
          allowAny
          anyLabel="Any service"
          anyDescription={`See every driver going to ${drop?.name ?? 'this campus'}`}
          campusName={drop?.name}
        />
      )}
      </div>
      {lookups.data && canContinue && (
        <StickyActionBar>
          <Button fullWidth onClick={onContinue}>Continue</Button>
        </StickyActionBar>
      )}
    </div>
  );
}
